import { TaskboardViewComponent } from './taskboard-view.component';
import { SynchronizationService } from '../../services/synchronization.service';
import { Injectable } from '@angular/core';
import { Task } from '../../models/task';
import { UserstoryService } from '../../services/userstory.service';
import { SettingsService } from '../../services/settings.service';
import { IssuecardComponent } from '../../components/issuecard/issuecard.component';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { SyncEventType } from '../../models/enums/syncEventType';
import { EventIssuecardOpened } from '../../models/syncEvents/realtimeEvents/eventIssuecardOpened';
import { EventIssuecardToForeground } from '../../models/syncEvents/realtimeEvents/eventIssuecardToForeground';
import { ActionViewSynchroHelper } from '../actionview-synchro-helper';
import { EventIssueDeleted } from '../../models/syncEvents/persistenceEvents/eventIssueDeleted';
import { EventIssueCreated } from '../../models/syncEvents/persistenceEvents/eventIssueCreated';
import {EventIssueOpened} from '../../models/syncEvents/persistenceEvents/eventIssueOpened';
import {EventIssueClosed} from '../../models/syncEvents/persistenceEvents/eventIssueClosed';

/**
 * This class is encapsulating the handling of synchronsation-events for the taskboard-component
 */
@Injectable()
export class TaskboardSynchroHelper {
  constructor(
    private syncService: SynchronizationService,
    private actionViewSync: ActionViewSynchroHelper,
    private userStoryService: UserstoryService,
    private settingsService: SettingsService,
    private dynamicElementService: DynamicElementService
  ) { }

  public initSyncEventListeners(taskboard: TaskboardViewComponent) {
    // Init events shared by all boards
    this.actionViewSync.initSyncEventListeners(taskboard);
    // Init events specific to this board
    this.initTaskcardMovedSubscription(taskboard);
    this.initTaskcardDragStoppedSubscription(taskboard);
    this.initOpenIssuecardSubscription(taskboard);
    this.initIssueCardClickedSubscription(taskboard);
    this.initCreatedNewSubtaskSubscription(taskboard);
    this.initDeleteIssueSubscription(taskboard);
    this.initOpenedIssueSubscription(taskboard);
    this.initClosedIssueSubscription(taskboard);
    this.initIssueFocusSubscription(taskboard);
  }
  private initOpenIssuecardSubscription(taskboard: TaskboardViewComponent) {
    taskboard.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUECARD_OPENED).subscribe(payload => {
      const event = EventIssuecardOpened.fromPayload(payload);
      this.dynamicElementService.getAllIssueCards().forEach(card => card.removeAvatar(event.avatar));
      if (event.isNewIssue && !event.isSubissue) {
        taskboard.openNewIssueDialog(null, event);
        return;
      }
      // Open new-subissue card
      if (event.isNewIssue && event.isSubissue) {
        taskboard.openNewSubissueDialog(event.parentIssueId, null, event);
        return;
      }
      // Open card for already existing issue
      const issueComponent = taskboard.getIssueComponent(event.issueId);
      if (issueComponent) {
        issueComponent.onRemoteCardOpened(event);
      }
    }));
  }

  private initIssueCardClickedSubscription(taskboard: TaskboardViewComponent) {
    taskboard.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUECARD_TO_FOREGROUND).subscribe(payload => {
      const event = EventIssuecardToForeground.fromPayload(payload);
      this.dynamicElementService.getAllIssueCards().forEach(card => card.removeAvatar(event.avatar));
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (card) {
        card.updateAvatare(event.avatar);
        this.dynamicElementService.select(card.componentRef);
      }
    }));
  }
  private initIssueFocusSubscription(taskboard: TaskboardViewComponent) {
    taskboard.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUECARD_FOCUS).subscribe(payload => {
      const event = EventIssuecardToForeground.fromPayload(payload);
      taskboard.getAllIssueComponent().forEach(card => card.removeAvatar(event.avatar));
      this.dynamicElementService.getAllIssueCards().forEach(card => card.removeAvatar(event.avatar));
      const issuecard: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (issuecard) {
        issuecard.updateAvatare(event.avatar);
      }
      const card = taskboard.getIssueComponent(event.issueId);
      if (card) {
        card.updateAvatare(event.avatar);
      }
    }));
  }

  private initDeleteIssueSubscription(taskboard: TaskboardViewComponent) {
    taskboard.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUE_DELETED).subscribe(payload => {
      const event = EventIssueDeleted.fromPayload(payload);
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (card) {
        card.closeThisCard();
      }
      if (event.isSubissue) {
        taskboard.getIssue(event.issueId, true);
      } else {
        taskboard.removeParentIssue(event.issueId);
      }
    }));
  }

  private initCreatedNewSubtaskSubscription(taskboard: TaskboardViewComponent) {
    taskboard.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUE_CREATED).subscribe(payload => {
      const event = EventIssueCreated.fromPayload(payload);
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.temporaryIssueId);
      // Close the issuecard of the new issue
      if (card) {
        card.discardChangesAndCloseThisCard();
      }
      // Load the newly created iddue and add it to the boards datamodel
      if (event.isSubissue) {
        this.userStoryService.getSubissue(this.settingsService.getProjectName(), event.issueId).subscribe(newSubissue => {
          // TODO for some reason the task returned by pywall has the wrong parentid set, we should fix this but until
          // then we overwrite it here.
          newSubissue.parentIssueId = event.parentIssueId;
          taskboard.placeIssueInTaskboardDataModel(newSubissue);
        });
      }
      else {
        this.userStoryService.getParentIssue(this.settingsService.getProjectName(), event.issueId).subscribe(newIssue => {
          taskboard.onNewIssueCreated(newIssue);
        });
      }
    }));
  }
  private initOpenedIssueSubscription(taskboard: TaskboardViewComponent) {
    taskboard.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUE_OPENED).subscribe(payload => {
      const event = EventIssueOpened.fromPayload(payload);
      taskboard.getAllIssueComponent().forEach(card => card.removeAvatar(event.avatar));
      this.dynamicElementService.getAllIssueCards().forEach(card => card.removeAvatar(event.avatar));
      const issuecard: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (issuecard) {
        issuecard.updateAvatare(event.avatar);
      }
      const card = taskboard.getIssueComponent(event.issueId);
      card.updateAvatare(event.avatar);}));
  }
  private initClosedIssueSubscription(taskboard: TaskboardViewComponent) {
    taskboard.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUE_CLOSED).subscribe(payload => {
      const event = EventIssueClosed.fromPayload(payload);
      taskboard.getIssueComponent(event.issueId).removeAvatar(event.avatar);
      const issuecard: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (issuecard) {
        issuecard.removeAvatar(event.avatar);
      }
    }));
  }

  /* TODO CHECK / UPDATE ALL FUNCTIONS BELOW */
  private initTaskcardMovedSubscription(taskboard: TaskboardViewComponent) {
    taskboard.subscriptions.push(this.syncService.subscribeEvent<any>('TaskcardDragged').subscribe(data => {
      const task: Task = <Task>(taskboard.getIssue(data.taskId));
      // TODO reimplement
      /*
      if (task) {
        const element: HTMLElement = taskboard.createDraggedCardPreview(task);
        const container: HTMLElement = taskboard.getDraggedCardPreviewContainer();
        this.positionHelper.setRelativePosition(element, data.position.left, data.position.top, container);
      }
      */
    }));
  }

  private initTaskcardDragStoppedSubscription(taskboard: TaskboardViewComponent) {
    taskboard.subscriptions.push(this.syncService.subscribeEvent<any>('TaskcardDragStopped').subscribe(data => {
      const task: Task = <Task>(taskboard.getIssue(data.taskId));
      if (task) {
        taskboard.stopDragTaskCard(data.taskId);
      }
    }));
  }


}
